"use client";

/**
 * The project-channel half of the card: either a link to the existing Slack
 * channel (plus the two admin controls) or the create form.
 */

import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { EmptyState } from "@west-tennessee-consulting/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import type { DealProjectChannelState } from "./useDealProjectChannel";

export function ProjectChannelCard({
  state,
  isAdmin,
}: {
  state: DealProjectChannelState;
  isAdmin: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Project channel</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {state.initializing ? (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-transparent" />
            Checking project channel…
          </div>
        ) : state.statusError ? (
          /* The read FAILED, so we do not know whether a channel exists. Never
             fall through to the create form here — that is how a deal with a
             channel got offered a second one. */
          <StatusReadFailed state={state} />
        ) : state.hasChannel ? (
          <ExistingChannel state={state} isAdmin={isAdmin} />
        ) : (
          <CreateChannelForm state={state} />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Blocked state for a failed channel-status read. Offers the remediation IN
 * PLACE (a Retry button), matching `GoogleDriveAccessRequiredState` — see the
 * ERROR_STATES_ACTIONABLE rule.
 */
function StatusReadFailed({ state }: { state: DealProjectChannelState }) {
  return (
    <EmptyState
      icon={AlertTriangle}
      title="Couldn't load the project channel"
      description={`${state.statusError} Until this loads we can't tell whether this deal already has a channel, so creating one is disabled.`}
      action={
        <Button type="button" variant="outline" onClick={state.retryStatus}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      }
    />
  );
}

/** Read-only: a project either has a channel or not — not inline-editable. */
function ExistingChannel({
  state,
  isAdmin,
}: {
  state: DealProjectChannelState;
  isAdmin: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        This project has a dedicated Slack channel. The customer chats with the project team from
        their Hub portal; messages land in the channel.
      </p>
      {state.channelUrl ? (
        <a
          href={state.channelUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium text-[color:var(--primary)] hover:bg-muted"
        >
          🗂️ Open project channel in Slack
        </a>
      ) : (
        <p className="text-sm text-green-700">Project channel is set up.</p>
      )}
      {isAdmin && <ChannelAdminControls state={state} />}
      {state.channelMsg && <p className="text-sm text-green-700">{state.channelMsg}</p>}
    </div>
  );
}

function ChannelAdminControls({ state }: { state: DealProjectChannelState }) {
  return (
    <div className="space-y-3 border-t pt-3">
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          If the Meridian bot is the only member of the channel, re-add the WTC owners (Eliza,
          Charlie, Miclain). Already-present owners are skipped.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={state.reinviteOwners}
          disabled={state.reinviting}
        >
          {state.reinviting ? "Re-adding owners…" : "Re-add owners to channel"}
        </Button>
      </div>
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Need to start over? Unlink this channel from the deal — the Slack channel stays in Slack,
          the chat link resets, and you can create a fresh one.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={state.unlinkChannel}
          disabled={state.unlinking}
        >
          {state.unlinking ? "Unlinking…" : "Unlink / reset channel"}
        </Button>
      </div>
    </div>
  );
}

function CreateChannelForm({ state }: { state: DealProjectChannelState }) {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        Create a dedicated Slack channel for this project. The customer chats with the project team
        from their Hub portal; messages land in the channel.
      </p>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="project-slug">
            Project slug
          </label>
          {/*
            TYPING_FOCUS_STABILITY: the slug field is never disabled by a
            background flag — only the submit button reflects `creating`.
          */}
          <input
            id="project-slug"
            value={state.slug}
            onChange={(e) => state.setSlug(e.target.value)}
            placeholder="e.g. website-rebuild"
            className="w-full rounded-lg border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--primary)]"
          />
        </div>
        <Button type="button" onClick={state.createChannel} disabled={state.creating}>
          {state.creating ? "Creating…" : "Create channel"}
        </Button>
      </div>
      {state.channelMsg && <p className="text-sm text-green-700">{state.channelMsg}</p>}
    </>
  );
}
